import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { MapPin, Search, Plus, Lock } from "lucide-react";
import { Input } from "../ui/input";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { cn } from "../../lib/utils";
import type { Id } from "convex/_generated/dataModel";

interface VenueSelectorProps {
  value: Id<"venues"> | null;
  venueName: string;
  venueAddress: string;
  onSelect: (venue: {
    id: Id<"venues">;
    name: string;
    address: string;
    latitude?: number;
    longitude?: number;
  }) => void;
  onClear: () => void;
  onManualEntry: (query: string) => void;
}

export function VenueSelector({
  value,
  venueName,
  venueAddress,
  onSelect,
  onClear,
  onManualEntry,
}: VenueSelectorProps) {
  const { isLoggedIn } = useCurrentUser();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const searchResults = useQuery(
    api.venues.searchVenues,
    query.length >= 2 ? { query } : "skip"
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => setIsOpen(false);
    if (isOpen) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [isOpen]);

  if (value) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-secondary/20">
        <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{venueName}</p>
          {venueAddress && (
            <p className="text-xs text-muted-foreground truncate">{venueAddress}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search for a venue..."
          className="pl-9"
        />
      </div>

      {isOpen && query.length >= 2 && (
        <div className="absolute z-10 top-full mt-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          {searchResults === undefined ? (
            <div className="p-3 text-sm text-muted-foreground">Searching...</div>
          ) : searchResults.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">
              No venues found.
            </div>
          ) : (
            <ul className="max-h-48 overflow-y-auto">
              {searchResults.map((venue) => {
                // A claimed venue posts its own programme. Show it — so people
                // can see we know about it and stop hunting — but don't let it
                // be selected. submitEvent refuses these anyway; this is so
                // nobody fills in a whole form before finding that out.
                const claimed =
                  venue.claimStatus === "verified" && !!venue.ownedByUserId;

                if (claimed) {
                  return (
                    <li
                      key={venue._id}
                      className="px-4 py-2 border-b border-border last:border-b-0"
                    >
                      <div className="flex items-start gap-2">
                        <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-muted-foreground">
                            {venue.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            This venue posts its own events.
                          </p>
                          <Link
                            href={isLoggedIn ? `/venue/${venue._id}` : "/login"}
                            className="text-xs text-primary hover:underline"
                            onClick={() => setIsOpen(false)}
                          >
                            {isLoggedIn
                              ? "Run events here? Request posting access"
                              : "Do you manage this venue? Sign in"}
                          </Link>
                        </div>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={venue._id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect({
                          id: venue._id,
                          name: venue.name,
                          address: venue.address,
                          latitude: venue.latitude,
                          longitude: venue.longitude,
                        });
                        setQuery("");
                        setIsOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-4 py-2 hover:bg-secondary/50 transition-colors",
                        "flex items-center gap-2"
                      )}
                    >
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{venue.name}</p>
                        <p className="text-xs text-muted-foreground">{venue.address}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => {
              onManualEntry(query);
              setIsOpen(false);
              setQuery("");
            }}
            className="w-full text-left px-4 py-2 border-t border-border hover:bg-secondary/50 transition-colors flex items-center gap-2 text-sm text-primary"
          >
            <Plus className="w-4 h-4" />
            Enter venue manually
          </button>
        </div>
      )}
    </div>
  );
}
